import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { hubDashboard } from "@/lib/api";
import { ArrowRight } from "lucide-react";

export default function Dashboard() {
  const { data, isLoading } = useQuery({ queryKey: ["hub-dashboard"], queryFn: hubDashboard });
  if (isLoading) return <p className="cv-body text-[#9A938A]">Loading...</p>;
  const next = data?.next_step;

  return (
    <div data-testid="hub-dashboard">
      <p className="cv-kicker">Dashboard</p>
      <h1 className="cv-display text-3xl mt-4">Welcome back{data?.user?.name ? `, ${data.user.name.split(" ")[0]}` : ""}.</h1>

      {!data?.engagement && (
        <p data-testid="hub-empty-intro" className="cv-body text-base text-[#1F1B16]/75 mt-5 max-w-2xl">
          This is your hub. Once an engagement is active, your roadmap, deliverables, and the next step live here. Until then, your Sprint, Operating Room archive, and event access are below.
        </p>
      )}

      {/* Next step */}
      {next && (
        <div data-testid="hub-next-step" className="mt-8 border border-[rgba(28,27,25,0.22)] rounded-[16px] overflow-hidden">
          <div className="cv-hairline-copper" />
          <div className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="cv-kicker">Your next step</p>
              <p className="cv-quote text-xl mt-2">{next.label}</p>
            </div>
            {next.external ? (
              <a href={next.href} target="_blank" rel="noopener noreferrer" className="cv-btn-primary inline-flex items-center gap-2">
                {next.label} <ArrowRight size={14} />
              </a>
            ) : (
              <Link to={next.href} className="cv-btn-primary inline-flex items-center gap-2">
                Continue <ArrowRight size={14} />
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Engagement */}
      <div className="mt-8">
        <p className="cv-kicker">Active engagement</p>
        {data?.engagement ? (
          <div className="mt-4 border border-[rgba(28,27,25,0.14)] rounded-[14px] p-6">
            <h3 className="cv-h2 text-base">{data.engagement.name}</h3>
            {data.engagement.summary ? (
              <p data-testid="hub-engagement-summary" className="cv-body text-sm text-[#1F1B16]/75 mt-2">
                {data.engagement.summary}
              </p>
            ) : (
              <p className="cv-body text-sm text-[#1F1B16]/70 mt-2">
                Status: {data.engagement.status}. Intake {data.engagement.intake_done ? "complete" : "pending"}.
              </p>
            )}
          </div>
        ) : (
          <p className="cv-body text-sm text-[#9A938A] mt-3">No active engagement yet. Start a Sprint to begin.</p>
        )}
      </div>

      {/* Orders */}
      {data?.orders?.length > 0 && (
        <div className="mt-8">
          <p className="cv-kicker">Your purchases</p>
          <div className="mt-4 divide-y divide-[rgba(28,27,25,0.14)] border-t border-[rgba(28,27,25,0.14)]">
            {data.orders.map((o) => (
              <div key={o.id} className="py-3 flex items-center justify-between">
                <span className="cv-body text-sm">{o.product_key}</span>
                <span className="cv-serif text-sm text-[#1F1B16]/70">${o.amount}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
